'use client'

import { useCallback, useMemo } from 'react'
import type React from 'react'

import { faHandshakeSimple } from '@fortawesome/pro-regular-svg-icons'
import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'

import {
  EmptyState,
  FormattedMessage,
  Link,
  showSnackbar,
  SpinnerDots,
  AnalyticsEntityType,
} from '@b-stock/fe-core/bstock-react'

import ToastContent from '@/buyer-portal/components/StyledToastContainer/ToastContent'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'
import TrackPageView from '@/helpers/telemetry/TrackPageView'
import { useAcceptTermsRestrictions } from '@/helpers/termsRestrictions/useAcceptTermsRestrictions'
import acceptedPoliciesQuery from '@/queries/acceptedPoliciesQuery'

export const TERMS_POINTS_WIDTH_REM = 42

export const TermsRestrictions = ({
  listingId,
  policy,
  termsDocUrl = '#',
  termsDisplayName = 'Terms of Purchase',
  termsPoints,
  sellerDisplayName = 'Seller',
  storeFrontName = 'Seller',
}: {
  listingId?: string
  policy: string
  termsDisplayName?: string
  termsDocUrl?: string
  termsPoints?: React.ReactNode
  sellerDisplayName?: string | null
  storeFrontName?: string
}) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const { trackButtonClicked } = useAnalytics()

  const handleSuccess = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [acceptedPoliciesQuery.name],
    })

    // Refreshing should retrigger the parent components that need to re-evaluate their state
    // If the user has successfully accepted the terms, they should not see this page again
    router.refresh()
  }, [router, queryClient])

  const handleError = useCallback((error: unknown) => {
    console.error('Failed to accept policy:', error)
    showSnackbar({
      content: (
        <ToastContent title="Error">
          <FormattedMessage id="Common.termsRestrictions.errorAccepting" />
        </ToastContent>
      ),
      options: { autoClose: true, type: 'error' },
    })
  }, [])

  const { isPending, acceptPolicy } = useAcceptTermsRestrictions(
    policy,
    handleSuccess,
    handleError
  )

  const handleAccept = useCallback(() => {
    trackButtonClicked({
      buttonName: 'accept_seller_opt_in',
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listingId,
      seller_display_name: sellerDisplayName,
      storefront_name: storeFrontName,
    })
    acceptPolicy()
  }, [
    acceptPolicy,
    trackButtonClicked,
    listingId,
    sellerDisplayName,
    storeFrontName,
  ])

  const handleCancel = useCallback(() => {
    trackButtonClicked({
      buttonName: 'cancel_seller_opt_in',
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listingId,
      seller_display_name: sellerDisplayName,
      storefront_name: storeFrontName,
    })
    router.push('/all-auctions')
  }, [router, trackButtonClicked, listingId, sellerDisplayName, storeFrontName])

  const handleAgreementLinkClick = useCallback(() => {
    trackButtonClicked({
      buttonName: 'read_agreement_seller_opt_in',
      entityType: AnalyticsEntityType.BUYER,
      listing_id: listingId,
      seller_display_name: sellerDisplayName,
      storefront_name: storeFrontName,
    })
  }, [trackButtonClicked, listingId, sellerDisplayName, storeFrontName])

  const primaryButton = useMemo(
    () => ({
      handleClick: handleAccept,
      label: isPending ? (
        <SpinnerDots />
      ) : (
        <FormattedMessage id="Common.termsRestrictions.accept" />
      ),
      disabled: isPending,
    }),
    [handleAccept, isPending]
  )

  const secondaryButton = useMemo(
    () => ({
      handleClick: handleCancel,
      label: <FormattedMessage id="Common.cancel" />,
      disabled: isPending,
    }),
    [handleCancel, isPending]
  )

  const description = (
    <>
      <FormattedMessage
        id="Common.termsRestrictions.description"
        values={{
          link: (chunks: React.ReactNode) => (
            <Link
              target="_blank"
              href={termsDocUrl}
              onClick={handleAgreementLinkClick}
            >
              {chunks}
            </Link>
          ),
          name: termsDisplayName,
        }}
      />
      {termsPoints}
    </>
  )

  return (
    <div data-testid="TermsRestrictions">
      <TrackPageView
        pageName="seller_opt_in"
        additionalData={{
          listing_id: listingId,
          seller_display_name: sellerDisplayName,
          storefront_name: storeFrontName,
        }}
      />
      <EmptyState
        size={4}
        icon={faHandshakeSimple}
        title={<FormattedMessage id="Common.termsRestrictions.title" />}
        description={description}
        primaryButton={primaryButton}
        secondaryButton={secondaryButton}
        maxWidthRem={termsPoints ? TERMS_POINTS_WIDTH_REM : undefined}
      />
    </div>
  )
}
